import React, { useState } from 'react';
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { CameraIcon, AdjustmentsHorizontalIcon, ClockIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { useAggregatedStats } from '../hooks/useAPI';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

export const AggregatedStatisticsView: React.FC = () => {
  const { stats, loading, error } = useAggregatedStats();
  const [selectedCamera, setSelectedCamera] = useState<string | null>(null);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-neutral-600 dark:text-neutral-400">Loading statistics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center text-red-600 dark:text-red-400">
          <p className="text-lg font-semibold mb-2">Error loading statistics</p>
          <p className="text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (!stats || stats.total_photos === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center text-neutral-600 dark:text-neutral-400">
          <CameraIcon className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <p className="text-lg font-semibold">No photos yet</p>
          <p className="text-sm">Upload some photos to see statistics</p>
        </div>
      </div>
    );
  }

  // Transform data for charts
  const cameraData = Object.entries(stats.cameras)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);

  const lensData = Object.entries(stats.lenses)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 10);

  const apertureData = Object.entries(stats.apertures)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => parseFloat(a.name.substring(2)) - parseFloat(b.name.substring(2)));

  const isoData = Object.entries(stats.iso)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => parseInt(a.name.substring(4)) - parseInt(b.name.substring(4)));

  const focalLengthData = Object.entries(stats.focal_lengths)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => parseInt(a.name) - parseInt(b.name));

  const shutterSpeedData = Object.entries(stats.shutter_speeds)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => {
      const aVal = parseFloat(a.name.split('/')[1] || a.name);
      const bVal = parseFloat(b.name.split('/')[1] || b.name);
      return bVal - aVal;
    })
    .slice(0, 15);

  const timeOfDayData = stats.time_of_day.map((count, hour) => ({
    hour: `${hour}:00`,
    count
  }));

  return (
    <div className="p-6 space-y-6 bg-neutral-50 dark:bg-neutral-900">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-neutral-900 dark:text-neutral-100">
            Photo Statistics
          </h2>
          <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
            Analyzing {stats.total_photos.toLocaleString()} photos
            {stats.last_updated && (
              <span className="ml-2">
                · Updated {new Date(stats.last_updated).toLocaleTimeString()}
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Camera Distribution */}
        <div className="bg-white dark:bg-neutral-800 rounded-xl p-6 border border-neutral-200 dark:border-neutral-700">
          <div className="flex items-center gap-2 mb-4">
            <CameraIcon className="w-5 h-5 text-neutral-600 dark:text-neutral-400" />
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
              Camera Bodies
            </h3>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={cameraData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={100}
                label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                onClick={(data) => setSelectedCamera(data.name)}
                style={{ cursor: 'pointer' }}
              >
                {cameraData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                    opacity={selectedCamera && selectedCamera !== entry.name ? 0.3 : 1}
                  />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1f2937',
                  border: 'none',
                  borderRadius: '0.5rem',
                  color: '#fff'
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Lens Distribution */}
        <div className="bg-white dark:bg-neutral-800 rounded-xl p-6 border border-neutral-200 dark:border-neutral-700">
          <div className="flex items-center gap-2 mb-4">
            <AdjustmentsHorizontalIcon className="w-5 h-5 text-neutral-600 dark:text-neutral-400" />
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
              Lenses Used
            </h3>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={lensData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis
                dataKey="name"
                angle={-45}
                textAnchor="end"
                height={120}
                tick={{ fill: '#9ca3af', fontSize: 12 }}
              />
              <YAxis tick={{ fill: '#9ca3af' }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1f2937',
                  border: 'none',
                  borderRadius: '0.5rem',
                  color: '#fff'
                }}
              />
              <Bar dataKey="value" fill="#10b981" opacity={0.9} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Aperture Distribution */}
        <div className="bg-white dark:bg-neutral-800 rounded-xl p-6 border border-neutral-200 dark:border-neutral-700">
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
            Aperture Settings
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={apertureData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="name" tick={{ fill: '#9ca3af' }} />
              <YAxis tick={{ fill: '#9ca3af' }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1f2937',
                  border: 'none',
                  borderRadius: '0.5rem',
                  color: '#fff'
                }}
              />
              <Bar dataKey="value" fill="#8b5cf6" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* ISO Distribution */}
        <div className="bg-white dark:bg-neutral-800 rounded-xl p-6 border border-neutral-200 dark:border-neutral-700">
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
            ISO Usage
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={isoData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="name" tick={{ fill: '#9ca3af' }} angle={-45} textAnchor="end" height={80} />
              <YAxis tick={{ fill: '#9ca3af' }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1f2937',
                  border: 'none',
                  borderRadius: '0.5rem',
                  color: '#fff'
                }}
              />
              <Bar dataKey="value" fill="#f59e0b" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Focal Length Distribution */}
        <div className="bg-white dark:bg-neutral-800 rounded-xl p-6 border border-neutral-200 dark:border-neutral-700">
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
            Focal Length
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={focalLengthData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="name" tick={{ fill: '#9ca3af' }} angle={-45} textAnchor="end" height={80} />
              <YAxis tick={{ fill: '#9ca3af' }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1f2937',
                  border: 'none',
                  borderRadius: '0.5rem',
                  color: '#fff'
                }}
              />
              <Bar dataKey="value" fill="#3b82f6" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Shutter Speed Distribution */}
        <div className="bg-white dark:bg-neutral-800 rounded-xl p-6 border border-neutral-200 dark:border-neutral-700">
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
            Shutter Speed
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={shutterSpeedData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="name" tick={{ fill: '#9ca3af' }} angle={-45} textAnchor="end" height={80} />
              <YAxis tick={{ fill: '#9ca3af' }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1f2937',
                  border: 'none',
                  borderRadius: '0.5rem',
                  color: '#fff'
                }}
              />
              <Bar dataKey="value" fill="#ec4899" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Shooting Time Analysis - Full Width */}
      <div className="bg-white dark:bg-neutral-800 rounded-xl p-6 border border-neutral-200 dark:border-neutral-700">
        <div className="flex items-center gap-2 mb-4">
          <ClockIcon className="w-5 h-5 text-neutral-600 dark:text-neutral-400" />
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
            Shooting Times (24-Hour)
          </h3>
        </div>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={timeOfDayData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis dataKey="hour" tick={{ fill: '#9ca3af' }} />
            <YAxis tick={{ fill: '#9ca3af' }} />
            <Tooltip
              contentStyle={{
                backgroundColor: '#1f2937',
                border: 'none',
                borderRadius: '0.5rem',
                color: '#fff'
              }}
            />
            <Bar dataKey="count" fill="#14b8a6" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* GPS Heatmap Placeholder */}
      {stats.gps_heatmap && stats.gps_heatmap.length > 0 && (
        <div className="bg-white dark:bg-neutral-800 rounded-xl p-6 border border-neutral-200 dark:border-neutral-700">
          <div className="flex items-center gap-2 mb-4">
            <MapPinIcon className="w-5 h-5 text-neutral-600 dark:text-neutral-400" />
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
              Photo Locations
            </h3>
            <span className="text-sm text-neutral-500">
              ({stats.gps_heatmap.length} locations)
            </span>
          </div>
          <div className="h-96 bg-neutral-100 dark:bg-neutral-700 rounded-lg flex items-center justify-center">
            <p className="text-neutral-500">GPS Heatmap - Coming next!</p>
          </div>
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-neutral-800 rounded-xl p-4 border border-neutral-200 dark:border-neutral-700">
          <div className="text-sm text-neutral-600 dark:text-neutral-400">Total Photos</div>
          <div className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 mt-1">
            {stats.total_photos.toLocaleString()}
          </div>
        </div>
        <div className="bg-white dark:bg-neutral-800 rounded-xl p-4 border border-neutral-200 dark:border-neutral-700">
          <div className="text-sm text-neutral-600 dark:text-neutral-400">Cameras</div>
          <div className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 mt-1">
            {cameraData.length}
          </div>
        </div>
        <div className="bg-white dark:bg-neutral-800 rounded-xl p-4 border border-neutral-200 dark:border-neutral-700">
          <div className="text-sm text-neutral-600 dark:text-neutral-400">Lenses</div>
          <div className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 mt-1">
            {Object.keys(stats.lenses).length}
          </div>
        </div>
        <div className="bg-white dark:bg-neutral-800 rounded-xl p-4 border border-neutral-200 dark:border-neutral-700">
          <div className="text-sm text-neutral-600 dark:text-neutral-400">GPS Tagged</div>
          <div className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 mt-1">
            {stats.gps_heatmap?.length || 0}
          </div>
        </div>
      </div>
    </div>
  );
};
